// src/scene/StageShell.tsx
/**
 * Stage Shell Component
 *
 * Standardized wrapper for all stage content.
 * Provides: positioning, activation gating, float animation, hero/secondary placement.
 * Uses drei helpers for visual depth and quality.
 * All stages must use this for consistency.
 */

import { useRef, ReactNode } from 'react'
import { Group, Mesh } from 'three'
import { useFrame } from '@react-three/fiber'
import { RoundedBox, Float } from '@react-three/drei'
import { Container } from '@react-three/uikit'
import { useControls, folder } from 'leva'
import { useIsStageActive, STAGE_ANCHORS, useStageStore } from './useStageStore'
import { usePanelLayout, useDesktopLayout, useDesktopPanelPositions, useResponsiveLayout, DESKTOP_PANEL_POSITIONS } from './useResponsiveLayout'
import { ui } from './ui'

// Base UIKit scale factor for world-space rendering
// At pixelSize 0.002, 300px = 0.6m (good proportion to ~1.8m avatar)
const BASE_PIXEL_SIZE = 0.002

// Reference height for scaling (1080p baseline)
const REFERENCE_HEIGHT = 1080

// Panel frame depth (in world units)
const FRAME_DEPTH = 0.02

// Convert Panel's borderRadius (ui.radius.xl = 18px) to world units
// Uses base pixel size (frame radius doesn't need dynamic scaling)
const FRAME_RADIUS = ui.radius.xl * BASE_PIXEL_SIZE // 0.036

// Small margin around the panel for the frame
const FRAME_MARGIN = 0.01

// ============================================================================
// STAGE SHELL
// ============================================================================

interface StageShellProps {
  /** Stage ID (must match STAGE_ANCHORS) */
  stageId: string
  /** Panel content (required) */
  panel: ReactNode
  /** Panel width in pixels (for UIKit) */
  panelWidth?: number
  /** Panel height in pixels (for UIKit) - auto if not specified */
  panelHeight?: number
  /** Scale multiplier for the panel */
  scale?: number
  /** Hero 3D object (optional, positioned prominently) */
  hero?: ReactNode
  /** Secondary 3D objects (optional, surrounding elements) */
  secondary?: ReactNode
  /** Background elements (optional, far from camera) */
  background?: ReactNode
  /** Custom panel position override */
  panelPosition?: [number, number, number]
  /** Custom panel rotation override */
  panelRotation?: [number, number, number]
}

// Slide distance for off-screen panels
const SLIDE_DISTANCE = 2.5

export function StageShell({
  stageId,
  panel,
  panelWidth = 320,
  panelHeight,
  scale = 1,
  hero,
  secondary,
  background,
  panelPosition,
  panelRotation,
}: StageShellProps) {
  const groupRef = useRef<Group>(null)
  const isActive = useIsStageActive(stageId)
  const currentIndex = useStageStore((s) => s.currentIndex)
  const { scale: layoutScale } = usePanelLayout()
  const { isDesktopLayout } = useDesktopLayout()
  const { height } = useResponsiveLayout()
  const frustumPositions = useDesktopPanelPositions()

  // Dynamic PIXEL_SIZE based on viewport height
  // Clamp between 0.8x and 1.2x base size for readability
  const pixelSizeScale = Math.max(0.8, Math.min(1.2, REFERENCE_HEIGHT / height))
  const PIXEL_SIZE = BASE_PIXEL_SIZE * pixelSizeScale

  // Render order: higher = renders later = appears on top with uikit's reversePainterSortStable
  const RENDER_ORDER: Record<string, number> = { intro: 1, projects: 1, skills: 2, experience: 3 }
  const renderOrder = RENDER_ORDER[stageId] ?? 0

  // Get stage anchor configuration
  const stageAnchor = STAGE_ANCHORS.find((s) => s.id === stageId)
  const stageIndex = STAGE_ANCHORS.findIndex((s) => s.id === stageId)

  // Get dynamic frustum-relative positions (updates with viewport changes)
  const frustumDefaults = frustumPositions[stageId as 'intro' | 'experience' | 'projects' | 'skills']
  const fallbackPos = DESKTOP_PANEL_POSITIONS[stageId as keyof typeof DESKTOP_PANEL_POSITIONS]

  // Use computed frustum position directly (not cached by Leva)
  const computedDesktopPos: [number, number, number] = (frustumDefaults && typeof frustumDefaults === 'object' && 'position' in frustumDefaults) ? frustumDefaults.position : fallbackPos?.position ?? [0, 0, 2]
  const computedDesktopRot: [number, number, number] = (frustumDefaults && typeof frustumDefaults === 'object' && 'rotation' in frustumDefaults) ? frustumDefaults.rotation : fallbackPos?.rotation ?? [0, 0, 0]

  // Get dynamic scale factor from frustum calculations (scales panels to fit viewport)
  const frustumScale = frustumPositions.scaleFactor ?? 1

  // Leva controls for manual offset adjustments only
  const stageLabel = stageId.charAt(0).toUpperCase() + stageId.slice(1)
  const isIntroOrProjects = stageId === 'intro' || stageId === 'projects'
  const {
    offsetX, offsetY, offsetZ,
    rotOffsetX, rotOffsetY, rotOffsetZ,
  } = useControls(`Desktop ${stageLabel}`, {
    'Position Offset': folder({
      offsetX: { value: 0, min: -1, max: 1, step: 0.01 },
      offsetY: { value: isIntroOrProjects ? 0.06 : stageId === 'experience' ? 0.03 : 0, min: -1, max: 1, step: 0.01 },
      offsetZ: { value: isIntroOrProjects ? -0.08 : stageId === 'experience' ? 0.02 : 0, min: -1, max: 1, step: 0.01 },
    }),
    'Rotation Offset': folder({
      rotOffsetX: { value: 0, min: -0.5, max: 0.5, step: 0.01 },
      rotOffsetY: { value: 0, min: -0.5, max: 0.5, step: 0.01 },
      rotOffsetZ: { value: 0, min: -0.5, max: 0.5, step: 0.01 },
    }),
  })

  // Desktop mode: use computed frustum positions + manual offsets
  // Mobile mode: use props from stage component
  const basePosition: [number, number, number] = isDesktopLayout
    ? [computedDesktopPos[0] + offsetX, computedDesktopPos[1] + offsetY, computedDesktopPos[2] + offsetZ]
    : (panelPosition ?? stageAnchor?.panelPosition ?? [0, 0, 0])
  const rotation: [number, number, number] = isDesktopLayout
    ? [computedDesktopRot[0] + rotOffsetX, computedDesktopRot[1] + rotOffsetY, computedDesktopRot[2] + rotOffsetZ]
    : (panelRotation ?? stageAnchor?.panelRotation ?? [0, 0, 0])

  // Convert pixel dimensions to world units
  const frameWidth = panelWidth * PIXEL_SIZE
  const frameHeight = panelHeight ? panelHeight * PIXEL_SIZE : frameWidth * 1.4 // Default aspect ratio

  // Combined scale: responsive layout scale * custom scale * frustum scale (desktop only)
  const combinedScale = layoutScale * scale * (isDesktopLayout ? frustumScale : 1)

  // Calculate slide offset based on shortest distance around the stage ring
  // Handles wrap-around: stage 2→0 (forward) slides left, 0→2 (backward) slides right
  const getSlideOffset = () => {
    if (isDesktopLayout) return 0 // No sliding in desktop mode

    const n = STAGE_ANCHORS.length
    let diff = stageIndex - currentIndex
    // Normalize to [-n/2, n/2) for shortest path around the ring
    if (diff > n / 2) diff -= n
    if (diff < -n / 2) diff += n

    if (diff < 0) return -SLIDE_DISTANCE // Behind current → slide left
    if (diff > 0) return SLIDE_DISTANCE  // Ahead of current → slide right
    return 0 // Current stage → centered
  }

  // Apply renderOrder to all descendant objects (Three.js doesn't inherit renderOrder)
  // renderOrder is static (derived from stageId), so throttle to once per second
  // instead of traversing every frame (60x/sec → 1x/sec)
  const lastTraverseTime = useRef(0)
  useFrame(({ clock }) => {
    if (!groupRef.current) return
    const now = clock.elapsedTime
    if (now - lastTraverseTime.current < 1) return
    lastTraverseTime.current = now
    groupRef.current.traverse((child) => {
      child.renderOrder = renderOrder
    })
  })

  // Animate position and scale (frame-rate independent)
  useFrame((_state, delta) => {
    if (!groupRef.current) return

    const slideOffset = getSlideOffset()

    // Target position
    const targetX = basePosition[0] + slideOffset
    const targetY = basePosition[1]
    const targetZ = isDesktopLayout ? basePosition[2] : (isActive ? basePosition[2] : basePosition[2] - 0.5)

    // Frame-rate independent lerp: 1 - exp(-speed * delta)
    // Gives consistent animation speed at 30fps, 60fps, or 120fps
    const speed = isDesktopLayout ? 5 : 8
    const t = 1 - Math.exp(-speed * delta)
    groupRef.current.position.x += (targetX - groupRef.current.position.x) * t
    groupRef.current.position.y += (targetY - groupRef.current.position.y) * t
    groupRef.current.position.z += (targetZ - groupRef.current.position.z) * t

    // In desktop mode, all panels are fully visible
    // In mobile mode, scale based on active state
    const targetScale = isDesktopLayout ? combinedScale : (isActive ? combinedScale : 0.5)
    const currentScale = groupRef.current.scale.x
    const newScale = currentScale + (targetScale - currentScale) * t
    groupRef.current.scale.setScalar(newScale)
  })

  return (
    <group
      ref={groupRef}
      position={[basePosition[0], basePosition[1], basePosition[2]]}
    >
      <Float
        speed={1.5}
        rotationIntensity={isActive ? 0.08 : 0}
        floatIntensity={isActive ? 0.2 : 0}
        floatingRange={[-0.015, 0.015]}
      >
        <group rotation={rotation}>
        {/* Panel Frame Backing - gives physical depth */}
        <RoundedBox
          args={[frameWidth + FRAME_MARGIN, frameHeight + FRAME_MARGIN, FRAME_DEPTH]}
          radius={FRAME_RADIUS}
          smoothness={4}
          position={[0, 0, -FRAME_DEPTH / 2]}
        >
          <meshPhysicalMaterial
            color={ui.colors.bg.deep}
            metalness={0.1}
            roughness={0.6}
            envMapIntensity={0.2}
          />
        </RoundedBox>

        {/* Edge highlight - subtle glow */}
        <RoundedBox
          args={[frameWidth + FRAME_MARGIN + 0.006, frameHeight + FRAME_MARGIN + 0.006, 0.002]}
          radius={FRAME_RADIUS + 0.003}
          smoothness={4}
          position={[0, 0, -FRAME_DEPTH - 0.001]}
        >
          <meshBasicMaterial
            color={ui.colors.primary}
            transparent
            opacity={0.15}
          />
        </RoundedBox>

        {/* Panel Layer (UI) */}
        <group position={[0, 0, 0.01]}>
          <Container
            sizeX={frameWidth}
            sizeY={frameHeight}
            pixelSize={PIXEL_SIZE}
            alignItems="center"
            justifyContent="center"
          >
            {panel}
          </Container>
        </group>

        {/* Hero Layer (prominent 3D object) */}
        {hero && (
          <group name="hero-layer" position={[0, 0, 0.1]}>
            {hero}
          </group>
        )}

        {/* Secondary Layer (supporting elements) */}
        {secondary && (
          <group name="secondary-layer">
            {secondary}
          </group>
        )}

        {/* Background Layer */}
        {background && (
          <group name="background-layer" position={[0, 0, -0.5]}>
            {background}
          </group>
        )}
        </group>
      </Float>
    </group>
  )
}

// ============================================================================
// HERO OBJECT WRAPPER
// ============================================================================

interface HeroObjectProps {
  children: ReactNode
  /** Position relative to stage */
  position?: [number, number, number]
  /** Rotation */
  rotation?: [number, number, number]
  /** Scale */
  scale?: number
  /** Enable idle rotation */
  rotate?: boolean
  /** Rotation speed */
  rotationSpeed?: number
}

export function HeroObject({
  children,
  position = [0.5, 0, 0.3],
  rotation = [0, 0, 0],
  scale = 1,
  rotate = false,
  rotationSpeed = 0.2,
}: HeroObjectProps) {
  const groupRef = useRef<Group>(null)

  useFrame((_state, delta) => {
    if (!groupRef.current || !rotate) return
    groupRef.current.rotation.y += delta * rotationSpeed
  })

  return (
    <group
      ref={groupRef}
      position={position}
      rotation={rotation}
      scale={scale}
    >
      {children}
    </group>
  )
}

// ============================================================================
// SECONDARY OBJECTS WRAPPER
// ============================================================================

interface SecondaryObjectsProps {
  children: ReactNode
  /** Arrangement pattern */
  pattern?: 'cluster' | 'arc' | 'line'
  /** Distance from center */
  radius?: number
  /** Number of items (for auto-arrangement) */
  count?: number
}

export function SecondaryObjects({
  children,
}: SecondaryObjectsProps) {
  // For now, just pass through - could auto-arrange in future
  return (
    <group position={[0, 0, 0]}>
      {children}
    </group>
  )
}

// ============================================================================
// DECORATIVE ELEMENTS
// ============================================================================

interface FloatingParticleProps {
  position?: [number, number, number]
  color?: string
  size?: number
  speed?: number
}

export function FloatingParticle({
  position = [0, 0, 0],
  color = '#648cdc',
  size = 0.02,
  speed = 1,
}: FloatingParticleProps) {
  const meshRef = useRef<Mesh>(null)

  useFrame((state) => {
    if (!meshRef.current) return
    const time = state.clock.elapsedTime * speed
    meshRef.current.position.y = position[1] + Math.sin(time) * 0.1
    meshRef.current.position.x = position[0] + Math.cos(time * 0.7) * 0.05
  })

  return (
    <mesh ref={meshRef} position={position}>
      <sphereGeometry args={[size, 8, 8]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.5}
        roughness={0.5}
        metalness={0.3}
      />
    </mesh>
  )
}

// ============================================================================
// STAGE GROUP (for organizing multiple stages)
// ============================================================================

interface StageGroupProps {
  children: ReactNode
}

export function StageGroup({ children }: StageGroupProps) {
  return (
    <group name="stages">
      {children}
    </group>
  )
}

export default StageShell
